import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Tooltip,
  Collapse,
  IconButton,
  Badge,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ChatIcon from '@mui/icons-material/Chat';
import { MessageHistory as MessageHistoryType } from '../../types';

interface MessageHistoryProps {
  messages: MessageHistoryType[];
  loading?: boolean;
}

interface ExpandableRowProps {
  message: MessageHistoryType;
  formatTimestamp: (timestamp: string) => string;
  getStatusColor: (status: string) => "success" | "error" | "warning" | "default";
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({ message, formatTimestamp, getStatusColor }) => {
  const [open, setOpen] = useState(false);
  const hasResponses = message.responses && message.responses.length > 0;

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          {hasResponses && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{formatTimestamp(message.timestamp)}</TableCell>
        <TableCell sx={{ maxWidth: '300px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {message.content}
        </TableCell>
        <TableCell>
          <Tooltip title={message.recipients.join(', ')}>
            <Chip 
              label={`${message.recipients.length} recipients`}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={message.status}
              size="small"
              color={getStatusColor(message.status)}
              sx={{ textTransform: 'capitalize' }}
            />
            {hasResponses && (
              <Badge badgeContent={message.responses!.length} color="primary">
                <ChatIcon color="action" fontSize="small" />
              </Badge>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Tooltip title={message.response || 'No response details'}>
            <Typography variant="body2" color="textSecondary" sx={{ 
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              cursor: 'help'
            }}>
              Batch: {message.batchId}
            </Typography>
          </Tooltip>
        </TableCell>
      </TableRow>
      {hasResponses && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '1rem' }}>
                  Responses
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Responder</TableCell>
                      <TableCell>Response</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {message.responses!.map((response, index) => (
                      <TableRow key={`${message.messageId}-response-${index}`}>
                        <TableCell>{formatTimestamp(response.timestamp)}</TableCell>
                        <TableCell>{response.responderName}</TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {response.response}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export const MessageHistory: React.FC<MessageHistoryProps> = ({
  messages,
  loading = false,
}) => {
  const getStatusColor = (status: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  };

  return (
    <Paper elevation={3} sx={{ height: '56vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Message History
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {messages.length} messages sent
        </Typography>
      </Box>
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Loading message history...
            </Typography>
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Date & Time</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length > 0 ? messages.map((msg) => (
                <ExpandableRow 
                  key={msg.messageId}
                  message={msg}
                  formatTimestamp={formatTimestamp}
                  getStatusColor={getStatusColor}
                />
              )) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No messages sent yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Paper>
  );
};

export default MessageHistory;
